<template>
    <div style="height: 200px">
        <line-chart id="research-reversion-slope" :chart-options="chartOptions" :chart-data="chartData" :plugins="plugins">Failed to load.</line-chart>
    </div>
</template>
<script>
    import { ref, inject, computed } from "vue";
    import lineChart from "@/components/research/line-chart.vue";
    import functions from "@/components/research/functions.js";
    import externalTooltip from "@/components/research/externalTooltip.js";

    export default {
        components: {
            lineChart
        },
        props: {
            startDate: {
                type: [Date, String, Object],
                default: () => {
                    return new Date(new Date().getFullYear() - 1, 1, 1)
                }
            },
            endDate: {
                type: [Date, String, Object],
                default: () => {
                    return new Date()
                }
            },
            customOptions: {
                type: Object,
                default: () => {
                    return { avgRange: 12, hideTooltip: false }
                }
            },
            rawData: {
                type: Array,
                default: () => {
                    return [];
                }
            },
            plugins: {
                type: Array,
                default: () => {
                    return [];
                }
            }
        },
        setup: (props, context) => {
            const moment = inject("$moment");

            const data = computed(() => {
                if (!props.startDate || !props.endDate) return props.rawData;
                else {
                    return props.rawData.filter(d => props.startDate <= moment(d["c_date"], "YYYY-MM-DD").toDate() && moment(d["c_date"], "YYYY-MM-DD").toDate() <= props.endDate);
                }
            });

            const getSlopeRange = (values) => {
                const clean = values.filter(v => v !== null && v !== undefined && isFinite(v));
                if (clean.length === 0) return { min: -0.5, max: 0.5 };

                const maxAbs = Math.max(...clean.map(v => Math.abs(v)));
                const padded = maxAbs === 0 ? 0.5 : maxAbs * 1.1;
                return { min: -padded, max: padded };
            };

            const chartOptions = computed(() => {
                const slopeValues = data.value.map(d => functions.reversion_slope(d));
                const slopeRange = getSlopeRange(slopeValues);

                return {
                    z_index: 3,
                    name: "Reversion Slope",
                    responsive: true,
                    maintainAspectRatio: false,
                    animation: false,
                    scales: {
                        x: {
                            ticks: {
                                callback: function (val, index) {
                                    const parts = this.getLabelForValue(val).split('-');
                                    const d = new Date(parts[0], parts[1] - 1, parts[2]);
                                    return moment(d).format("YYYY");
                                }
                            }
                        },
                        y: {
                            type: 'linear',
                            display: true,
                            position: 'right',
                            min: slopeRange.min,
                            max: slopeRange.max,
                            afterFit: function (scaleInstance) {
                                scaleInstance.width = 100;
                            },
                            grid: {
                                lineWidth: function (context) {
                                    if (context.tick.value == 0) return 1;
                                    else return 1;
                                },
                                color: function (context) {
                                    if (context.tick.value == 0) return 'black';
                                    else return 'rgba(0, 0, 0, 0.1)';
                                }
                            }
                        }
                    },
                    interaction: {
                        mode: 'index',
                        intersect: false
                    },
                    plugins: {
                        legend: {
                            position: "bottom"
                        },
                        tooltip: {
                            enabled: false,
                            position: "nearest",
                            hideTooltip: props.customOptions?.hideTooltip == true,
                            external: externalTooltip
                        }
                    },
                    layout: {
                        padding: {
                            left: 0
                        }
                    }
                };
            });

            const chartData = computed(() => {
                return {
                    labels: data.value.map(d => d["c_date"]),
                    datasets: [
                        {
                            label: "Reversion Slope (Ann.)",
                            fill: false,
                            borderColor: "darkorange",
                            data: data.value.map(d => functions.reversion_slope(d)),
                            yAxisID: 'y',
                            pointRadius: 0.1
                        }
                    ]
                };
            });

            return {
                chartOptions,
                chartData
            }
        }
    }
</script>
